/**
 * Google Maps Places API integration
 * Uses Google Maps JavaScript API for address autocomplete
 */

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_ID;
const GOOGLE_MAPS_SCRIPT_ID = "google-maps-script";



let googleMapsLoaded = false;
let autocompleteService = null;
let placesService = null;

/**
 * Load Google Maps JavaScript API script
 */
export function loadGoogleMapsScript() {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      googleMapsLoaded = true;
      initializeServices();
      resolve();
      return;
    }

    // Check if script is already being loaded
    if (document.getElementById(GOOGLE_MAPS_SCRIPT_ID)) {
      const checkInterval = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          clearInterval(checkInterval);
          googleMapsLoaded = true;
          initializeServices();
          resolve();
        }
      }, 100);
      return;
    }

    if (!GOOGLE_MAPS_API_KEY) {
      console.error("Google Maps API key not found. Make sure REACT_APP_GOOGLE_MAPS_API_ID is set in your .env file.");
      reject(new Error("Google Maps API key not configured. Please set REACT_APP_GOOGLE_MAPS_API_ID in your .env file."));
      return;
    }
    

    const script = document.createElement("script");
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&language=no`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      googleMapsLoaded = true;
      initializeServices();
      resolve();
    };

    script.onerror = (error) => {
      console.error("Failed to load Google Maps script:", error);
      reject(new Error("Failed to load Google Maps script. Check your API key and network connection."));
    };

    document.head.appendChild(script);
  });
}

/**
 * Initialize Google Maps services
 */
function initializeServices() {
  if (window.google && window.google.maps) {
    autocompleteService = new window.google.maps.places.AutocompleteService();
    placesService = new window.google.maps.places.PlacesService(
      document.createElement("div")
    );
  }
}

/**
 * Search for addresses using Google Places Autocomplete
 * @param {string} query - Search query
 * @param {Object} options - Additional options (country, location, etc.)
 * @returns {Promise<Array>} Array of address suggestions
 */
export async function searchAddresses(query, options = {}) {
  if (!query || query.trim().length < 2) {
    return [];
  }

  try {
    // Ensure Google Maps is loaded
    if (!googleMapsLoaded || !autocompleteService) {
      await loadGoogleMapsScript();
    }

    // Double-check that services are initialized
    if (!autocompleteService) {
      initializeServices();
    }

    return new Promise((resolve, reject) => {
      const request = {
        input: query,
        types: ["address"],
        language: "no",
        ...options,
      };

      // Restrict to Norway if no country specified
      if (!request.componentRestrictions) {
        request.componentRestrictions = { country: "no" };
      }

      autocompleteService.getPlacePredictions(request, (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          const results = predictions.map((prediction) => ({
            placeId: prediction.place_id,
            description: prediction.description,
            mainText: prediction.structured_formatting?.main_text || "",
            secondaryText: prediction.structured_formatting?.secondary_text || "",
            types: prediction.types || [],
          }));
          resolve(results);
        } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          resolve([]);
        } else if (status === window.google.maps.places.PlacesServiceStatus.REQUEST_DENIED) {
          const errorMsg = "Places API (legacy) is not enabled for your project. Please enable 'Places API' in Google Cloud Console: https://console.cloud.google.com/apis/library/places-backend.googleapis.com";
          console.error(errorMsg);
          reject(new Error(errorMsg));
        } else {
          console.error(`Places service error: ${status}`);
          reject(new Error(`Places service error: ${status}`));
        }
      });
    });
  } catch (error) {
    console.error("Error searching addresses:", error);
    return [];
  }
}

/**
 * Get place details by place ID
 * @param {string} placeId - Google Places place ID
 * @returns {Promise<Object>} Place details with formatted address components
 */
export async function getPlaceDetails(placeId) {
  try {
    // Ensure Google Maps is loaded
    if (!googleMapsLoaded || !placesService) {
      await loadGoogleMapsScript();
    }

    // Double-check that services are initialized
    if (!placesService) {
      initializeServices();
    }

    return new Promise((resolve, reject) => {
      const request = {
        placeId: placeId,
        fields: [
          "address_components",
          "formatted_address",
          "geometry",
          "name",
          "place_id",
        ],
      };

      placesService.getDetails(request, (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const addressComponents = {};
          
          // Parse address components
          if (place.address_components) {
            place.address_components.forEach((component) => {
              const types = component.types;
              if (types.includes("street_number")) {
                addressComponents.streetNumber = component.long_name;
              }
              if (types.includes("route")) {
                addressComponents.streetName = component.long_name;
              }
              if (types.includes("postal_code")) {
                addressComponents.postalCode = component.long_name;
              }
              if (types.includes("locality") || types.includes("postal_town")) {
                addressComponents.city = component.long_name;
              }
              if (types.includes("administrative_area_level_1")) {
                addressComponents.state = component.long_name;
              }
              if (types.includes("country")) {
                addressComponents.country = component.long_name;
                addressComponents.countryCode = component.short_name;
              }
            });
          }

          // Build full address line - street name first, then number (Norwegian format)
          const addressLine = [
            addressComponents.streetName,
            addressComponents.streetNumber,
          ]
            .filter(Boolean)
            .join(" ");

          resolve({
            placeId: place.place_id,
            formattedAddress: place.formatted_address,
            addressLine: addressLine,
            streetNumber: addressComponents.streetNumber || "",
            streetName: addressComponents.streetName || "",
            postalCode: addressComponents.postalCode || "",
            city: addressComponents.city || "",
            state: addressComponents.state || "",
            country: addressComponents.country || "",
            countryCode: addressComponents.countryCode || "",
            geometry: place.geometry,
            name: place.name,
          });
        } else {
          reject(new Error(`Places service error: ${status}`));
        }
      });
    });
  } catch (error) {
    console.error("Error getting place details:", error);
    throw error;
  }
}

